import { Box, Typography, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { PumpPipe } from 'src/assets/widgets/PumpPipe';
import { useAssetStateData } from 'src/features/sites/hooks';
import { PumpModeLabel } from 'src/features/widgets/components';
import { usePumpData } from 'src/features/widgets/components/LiftStationWidget/hooks';
import { PropelerPumpIcon } from 'src/features/widgets/components/PropelerPumpIcon';
import { useGetMultipleLatestReadings } from 'src/hooks/apiHooks/useGetMultipleLatestReadings';
import { usePumpStateAndMode } from 'src/hooks/featureHooks/usePumpStateAndMode';
import { useResponsive } from 'src/hooks/useResponsive';
import { AssignedAlarmTag } from 'src/types/assets';
import { ReadingEventMessage } from 'src/types/readings/readings';

type Props = {
  siteId?: string;
  customerId?: string;
  assetId: string;
  isStateMapped: boolean;
  messages: ReadingEventMessage;
  isTriplexPump: boolean;
  isQuadroplexPump: boolean;
};

export const LiftStationRotatorPump = ({
  siteId,
  customerId,
  assetId,
  isStateMapped,
  messages,
  isTriplexPump,
  isQuadroplexPump,
}: Props) => {
  const isTablet = useResponsive('between', 601, 1025);
  const isMobile = useResponsive('down', 600);
  const pumpHeight = isMobile
    ? isTriplexPump || isQuadroplexPump
      ? 60
      : 80
    : isTablet
    ? 100
    : 113;

  const [assetActiveAlarm, setAssetActiveAlarm] = useState<number | null>(null);
  const gatewayInputs = messages?.data?.data;

  const numberOfAllowedCharacters = 15;

  const { asset } = usePumpData({
    siteId: siteId!,
    customerId: customerId!,
    pumpAssetId: assetId,
  });

  const { assetState: liveActiveState, isRunningLive } = useAssetStateData({ asset: asset! });

  const titleLength = asset?.assetName?.length;

  const { readingsMap: multipleReadings } = useGetMultipleLatestReadings({
    items: asset?.assignedAlarmTags,
  });

  const values = Object.values(multipleReadings)?.map((tagData) => {
    const tagId = Object.keys(tagData)[0];
    const values = tagData[tagId]?.values ?? {};
    return Object.values(values)[0];
  });

  const isAlarmActive: boolean =
    assetActiveAlarm !== null
      ? assetActiveAlarm === 1
      : values.some((item) => String(item) === '1');

  const { activeState, isRunning } = usePumpStateAndMode({ asset: asset });

  const displayedState = liveActiveState ? liveActiveState : activeState;

  const currentIsRunning = isRunningLive ?? isRunning;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredAssignedAlarmTags: AssignedAlarmTag[] =
    asset?.assignedAlarmTags?.filter((tag: any) => tag?.assignedAlarmTagId) ?? [];

  useEffect(() => {
    if (filteredAssignedAlarmTags?.length > 0) {
      if (!gatewayInputs) {
        setAssetActiveAlarm(null);
        return;
      }

      let hasUndefined = false;

      const hasActiveAlarms = filteredAssignedAlarmTags.some(
        (assignedAlarmTag: AssignedAlarmTag) => {
          const findInput = gatewayInputs?.find(
            (input: { id: string | undefined }) => input.id === assignedAlarmTag.assignedAlarmTagId
          );

          if (!findInput) {
            hasUndefined = true;
            return false;
          }

          return Number(findInput.value) > 0;
        }
      );

      if (hasUndefined) return;

      setAssetActiveAlarm(hasActiveAlarms ? 1 : 0);
    }
  }, [filteredAssignedAlarmTags, gatewayInputs]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth:
          isMobile && isTriplexPump
            ? '37px'
            : isMobile
            ? '56px'
            : isQuadroplexPump
            ? '59px'
            : '66px',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box sx={{ display: 'flex', margin: isTriplexPump ? '0 0 -38px 18px' : '0 0 -38px 22px' }}>
        <PumpPipe
          height={isMobile ? 150 : 181}
          width={isMobile ? (isTriplexPump ? 25 : 35) : isQuadroplexPump ? 38 : 40}
        />
      </Box>

      <PropelerPumpIcon
        isRunMode={currentIsRunning}
        isAlarmActive={isAlarmActive}
        width={isMobile ? (isTriplexPump ? 40 : 40) : isQuadroplexPump ? 56 : 64}
        height={pumpHeight}
        propelerWidth={isMobile ? (isTriplexPump ? 20 : 28) : isQuadroplexPump ? 38 : 40}
        showRotator={isStateMapped}
        isMobile={isMobile}
        isTriplexPump={isTriplexPump}
      />
      <Tooltip title={asset?.assetName}>
        <Box sx={{ minHeight: '40px' }}>
          <Typography
            sx={{
              marginTop: isTablet ? (isTriplexPump ? '0px' : '5px') : '9px',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: '700',
              textAlign: 'center',
              lineHeight: '1',
            }}
          >
            {titleLength > numberOfAllowedCharacters
              ? asset?.assetName?.slice(0, numberOfAllowedCharacters) + '...'
              : asset?.assetName}
          </Typography>
        </Box>
      </Tooltip>

      <Box sx={{ marginTop: isMobile ? '8px' : isTablet ? '15px' : '25px' }}>
        <PumpModeLabel
          isRunMode={currentIsRunning}
          label={displayedState ?? 'Undefined'}
          isTriplexPump={isTriplexPump}
          isMobile={isMobile}
        />
      </Box>
    </Box>
  );
};
